import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AgGridReact } from "ag-grid-react";
import type { ColDef } from "ag-grid-community";
import { themeQuartz } from "ag-grid-community";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import { ArrowLeft, Plus, FileText, Edit, Eye } from "lucide-react";
import { useKPIStore } from "../../store/kpiStore";
import { Dropdown } from "../../components/dropdown/Dropdown";
import { usePremisesStore } from "../../store/store";

const gridTheme = themeQuartz.withParams({
  backgroundColor: "#fff",
  foregroundColor: "#2a2a2a",
  headerTextColor: "#2a2a2a",
  headerBackgroundColor: "#E9F5FF",
  oddRowBackgroundColor: "rgba(0,0,0,0.03)",
  fontSize: "0.75rem",
  headerFontWeight: "bold",
});

const PAGE_SIZE = 10;

const KPIChecklistManagement: React.FC = () => {
  const navigate = useNavigate();
  const {
    assessments,
    loading,
    fetchAssessmentsPaged, // implement in store to return { data, count }
    createAssessment,
  } = useKPIStore();
  const { allPremises, fetchPremises, premiseMap } = usePremisesStore();
  const [page, setPage] = useState(1);
  const [count, setCount] = useState(0);

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [assessmentName, setAssessmentName] = useState("");
  const [assessmentDate, setAssessmentDate] = useState("");
  const [premiseId, setPremiseId] = useState("");
  const [submitError, setSubmitError] = useState("");

  const loadPage = useCallback(async () => {
    const res = await fetchAssessmentsPaged({ page, limit: PAGE_SIZE });
    if (res && typeof res.count === "number") setCount(res.count);
  }, [fetchAssessmentsPaged, page]);

  useEffect(() => {
    loadPage();
  }, [loadPage]);

  useEffect(() => {
    if (allPremises.length === 0) {
      fetchPremises();
    }
  }, []);



  const pages = Math.max(Math.ceil(count / PAGE_SIZE), 1);

  const rowData = useMemo(() => {
    return (assessments || []).map((d: any) => ({
      id: d.id || d._id,
      name: d.name,
      date: d.date,
      month: d.month,
      premiseId: d.premiseId || d.facilityId || "",
      status: d.status || "Draft",
      compliedPct: Number.isFinite(d.compliedPct) ? d.compliedPct : 0,
      partiallyPct: Number.isFinite(d.partiallyCompliedPct) ? d.partiallyCompliedPct : 0,
      notCompliedPct: Number.isFinite(d.notCompliedPct) ? d.notCompliedPct : 0,
      totalItems: d.totalItems || 0,
      createdAt: d.createdAt,
    }));
  }, [assessments]);

  const getStatusPill = (status: string) => {
    const map: Record<string, string> = {
      Completed: "bg-green-100 text-green-800",
      completed: "bg-green-100 text-green-800",
      "In Progress": "bg-yellow-100 text-yellow-800",
      "in progress": "bg-yellow-100 text-yellow-800",
      Draft: "bg-gray-100 text-gray-800",
      draft: "bg-gray-100 text-gray-800",
      Submitted: "bg-blue-100 text-blue-800",
      submitted: "bg-blue-100 text-blue-800",
      Inactive: "bg-red-100 text-red-800",
      inactive: "bg-red-100 text-red-800",
      Active: "bg-green-100 text-green-800",
      active: "bg-green-100 text-green-800",
    };
    return map[status] || "bg-gray-100 text-gray-800";
  };

  const columnDefs = useMemo<ColDef<any>[]>(
    () => [
      {
        field: "name",
        headerName: "Assessment Name",
        sortable: true,
        minWidth: 160,
        maxWidth: 220,
        flex: 1,
        headerStyle: { backgroundColor: "#E9F5FF" },
        filter: false,
        cellRenderer: (p: any) => (
          <div className="flex items-center">
            {/* <FileText className="w-4 h-4 text-gray-400 mr-2" /> */}
            <div className="truncate">
              <div className="font-medium text-gray-900 truncate">
                {p.value}
              </div>
              <div className="text-xs text-gray-500">
                {p.data?.date
                  ? `Date: ${p.data.date}`
                  : p.data?.month
                    ? `Month: ${p.data.month}`
                    : ""}
              </div>
            </div>
          </div>
        ),
        cellStyle: { padding: "10px 0 0 15px", textAlign: "left" },
      },
      {
        field: "premiseId",
        headerName: "Premise",
        sortable: true,
        minWidth: 120,
        maxWidth: 150,
        flex: 0,
        headerStyle: { backgroundColor: "#E9F5FF" },
        filter: false,
        cellStyle: { padding: "10px 5px 0 10px", textAlign: "left" },
        valueGetter: (p: any) => {
          const premise = premiseMap[p.data.premiseId];
          return premise ? premise : "N/A";
        },
      },
      {
        field: "status",
        headerName: "Status",
        sortable: true,
        minWidth: 90,
        maxWidth: 120,
        flex: 0,
        headerStyle: { backgroundColor: "#E9F5FF" },
        filter: false,
        cellRenderer: (p: any) => (
          <span
            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusPill(
              p.value
            )}`}
          >
            {p.value}
          </span>
        ),
        cellStyle: { padding: "10px 5px 0 5px", textAlign: "left" },
      },
      {
        field: "compliedPct",
        headerName: "Complied",
        sortable: true,
        minWidth: 85,
        maxWidth: 110,
        flex: 0,
        headerStyle: () => ({ backgroundColor: "#E9F5FF", color: "#1f2937", fontWeight: "600" }),
        filter: false,
        cellRenderer: (p: any) => (
          <div className="text-center px-1">
            <div className="text-sm font-semibold text-green-600">
              {p.value}%
            </div>
            {/* <div className="text-xs text-gray-500">
                {p.data.totalItems ? Math.round((p.value / 100) * p.data.totalItems) : 0} items
              </div> */}
          </div>
        ),
        cellStyle: () => ({ padding: "8px 4px", textAlign: "center", borderRight: "1px solid #f0f0f0", borderLeft: "1px solid #f0f0f0" }),
      },
      {
        field: "partiallyPct",
        headerName: "Partial Complied",
        sortable: true,
        minWidth: 85,
        // maxWidth: 100,
        flex: 0.5,
        headerStyle: () => ({ backgroundColor: "#E9F5FF", color: "#1f2937", fontWeight: "600" }),
        filter: false,
        cellRenderer: (p: any) => (
          <div className="text-center px-1">
            <div className="text-sm font-semibold text-yellow-600">
              {p.value}%
            </div>
            {/* <div className="text-xs text-gray-500">
                {p.data.totalItems ? Math.round((p.value / 100) * p.data.totalItems) : 0} items
              </div> */}
          </div>
        ),
        cellStyle: () => ({ padding: "8px 4px", textAlign: "center", borderRight: "1px solid #f0f0f0" }),
      },
      {
        field: "notCompliedPct",
        headerName: "Not Complied",
        sortable: true,
        minWidth: 85,
        // maxWidth: 100,
        flex: 0.5,
        headerStyle: () => ({ backgroundColor: "#E9F5FF", color: "#1f2937", fontWeight: "600" }),
        filter: false,
        cellRenderer: (p: any) => (
          <div className="text-center px-1">
            <div className="text-sm font-semibold text-red-600">
              {p.value}%
            </div>
            {/* <div className="text-xs text-gray-500">
                {p.data.totalItems ? Math.round((p.value / 100) * p.data.totalItems) : 0} items
              </div> */}
          </div>
        ),
        cellStyle: () => ({ padding: "8px 4px", textAlign: "center", borderRight: "1px solid #f0f0f0" }),
      },
      {
        field: "action",
        headerName: "Actions",
        sortable: false,
        minWidth: 90,
        maxWidth: 100,
        flex: 0,
        headerStyle: { backgroundColor: "#E9F5FF" },
        filter: false,
        cellRenderer: (params: any) => (
          <div className="flex gap-x-1 justify-center items-center h-full">
            <button
              onClick={() =>
                navigate(
                  `/safety-equipment/kpi-checklist-detail/${params.data.id}?mode=view`
                )
              }
              className="p-1.5 hover:bg-gray-100 rounded transition-colors"
              title="View Assessment"
            >
              <Eye className="w-4 h-4 text-gray-600" />
            </button>
            <button
              className="p-1.5 hover:bg-gray-100 rounded transition-colors"
              title="Edit Assessment"
              onClick={() =>
                navigate(
                  `/safety-equipment/kpi-checklist-detail/${params.data.id}?mode=edit`
                )
              }
            >
              <Edit className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        ),
        cellStyle: { padding: "8px 4px", textAlign: "center" },
      },
    ],
    [navigate, premiseMap]
  );

  const defaultColDef = useMemo<any>(() => {
    return {
      editable: false,
      filter: false,
      tooltipComponentParams: { color: "#ececec" },
    };
  }, []);

  const theme = useMemo<any>(() => gridTheme, []);

  return (
    <div className="px-2 py-4 mt-14 min-h-screen">
      <div className="flex justify-between items-center mb-4">
        <button
          onClick={() => navigate("/safety-equipment")}
          className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Equipment
        </button>
        <button
          className="flex items-center justify-center font-semibold gap-2 bg-[#0fa9a6] text-white border border-[#0fa9a6] px-4 py-2 rounded-full hover:bg-teal-600"
          onClick={() => setIsCreateModalOpen(true)}
        >
          <Plus size={20} /> Create Assessment
        </button>
      </div>

      <div className="tableBlock w-full">
        <div className="card">
          {rowData?.length > 0 ? (
            <div className="ag-theme-quartz w-full min-h-[480px] border-0 rounded-lg shadow-lg">
              <AgGridReact
                rowData={rowData}
                columnDefs={columnDefs}
                theme={theme}
                headerHeight={65}
                rowHeight={60}
                loading={loading}
                defaultColDef={defaultColDef}
                domLayout="autoHeight"
                pagination={true}
                paginationPageSize={PAGE_SIZE}
                paginationPageSizeSelector={[10, 20, 50]}
                paginationAutoPageSize={false}
              />
            </div>
          ) : (
            <div className="flex flex-col justify-center items-center h-64">
              <p className="text-gray-500 text-lg mb-4">No assessments found</p>
            </div>
          )}
        </div>
      </div>

      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">
              Create New Assessment
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Name
                </label>
                <input
                  type="text"
                  value={assessmentName}
                  onChange={(e) => setAssessmentName(e.target.value)}
                  placeholder="e.g., Fire Safety Audit – Oct 2025"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Date
                </label>
                <input
                  type="date"
                  value={assessmentDate}
                  onChange={(e) => setAssessmentDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Premise Name
                </label>
                <Dropdown
                  selectedValue={premiseId}
                  onChange={setPremiseId}
                  type="premises"
                />
              </div>

              {submitError && (
                <div className="text-sm text-red-600">{submitError}</div>
              )}
            </div>

            <div className="flex gap-3 mt-6 justify-end">
              <button
                onClick={() => setIsCreateModalOpen(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setSubmitError("");
                  if (!assessmentName.trim() || !assessmentDate || !premiseId) {
                    setSubmitError("Please enter name, date, and premise.");
                    return;
                  }
                  try {
                    const result = await createAssessment({
                      name: assessmentName.trim(),
                      date: assessmentDate,
                      premiseId,
                    });
                    setIsCreateModalOpen(false);
                    setAssessmentName("");
                    setAssessmentDate("");
                    setPremiseId("");
                    await loadPage();
                    const id =
                      result?.assessmentId ||
                      (useKPIStore.getState() as any).assessments?.[0]?.id;
                    if (id)
                      navigate(`/safety-equipment/kpi-checklist-detail/${id}?mode=edit`);
                  } catch (e: any) {
                    setSubmitError(
                      e?.message || "Failed to create assessment."
                    );
                  }
                }}
                disabled={
                  !assessmentName.trim() || !assessmentDate || !premiseId
                }
                className="px-4 py-2 bg-[#00A8A8] text-white rounded-full hover:bg-teal-600 transition disabled:opacity-60"
              >
                Create
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KPIChecklistManagement;
